import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CCard, CCardBody, CCardHeader, CForm, CFormLabel, CFormInput, CFormTextarea,
  CButton, CRow, CCol, CAlert, CSpinner, CFormCheck, CBadge,
  CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter,
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import {
  cilUser, cilTask, cilPhone, cilNotes, cilLaptop,
  cilSend, cilSave, cilArrowLeft, cilSearch, cilInfo, cilMoney, cilGift,
  cilImage, cilTrash, cilZoom,
} from '@coreui/icons';
import api from '../services/api';
import { useNotifications } from '../context/NotificationContext';
import SmsModal from '../components/SmsModal';
import WhatsAppModal from '../components/WhatsAppModal';

function resizeImageClient(file, maxPx = 1024) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const { width, height } = img;
      if (width <= maxPx && height <= maxPx) { resolve(file); return; }
      let w = width, h = height;
      if (w >= h) { h = Math.round(h * maxPx / w); w = maxPx; }
      else { w = Math.round(w * maxPx / h); h = maxPx; }
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('toBlob failed')), 'image/jpeg', 0.82);
    };
    img.onerror = reject;
    img.src = url;
  });
}

const STATUS_OPTIONS = ['Baru', 'Dalam Proses', 'Selesai', 'Dipulangkan', 'Menunggu Maklumbalas', 'Waiting to Pickup'];
const PRIORITY_OPTIONS = ['Biasa', 'Sederhana', 'Penting'];
const CONTACT_OPTIONS = ['SMS', 'EMAIL', 'TEL/PEJ', 'WHATSAPP'];
const EQUIPMENT_OPTIONS = ['Laptop', 'Desktop', 'Printer', 'Laptop Power Adapter', 'System Disc', 'Monitor', 'Keyboard', 'Mouse', 'Others'];

const EMPTY = {
  custid: '', jobcode: '', perhubungan: [], keutamaan: 'Biasa', catatan: '',
  jenis: '', kos: '', alat: [], peralatan_lain: '', status: 'Baru',
};

/** Display order for customer modal (legacy DB column names). */
const CUSTOMER_INFO_FIELDS = [
  { key: 'nama', label: 'Name' },
  { key: 'uid', label: 'Customer UID' },
  { key: 'user_id', label: 'User ID' },
  { key: 'kp', label: 'IC / ID (KP)' },
  { key: 'sykt', label: 'Company (Sykt)' },
  { key: 'telhp', label: 'Mobile' },
  { key: 'telpej', label: 'Office phone' },
  { key: 'email', label: 'Email' },
  { key: 'alamat1', label: 'Address line 1' },
  { key: 'alamat2', label: 'Address line 2' },
  { key: 'poskod', label: 'Postcode' },
  { key: 'daerah', label: 'District' },
  { key: 'negeri', label: 'State' },
  { key: 'catatan', label: 'Notes' },
  { key: 'regdate', label: 'Registered date' },
  { key: 'regby', label: 'Registered by' },
  { key: 'date', label: 'Record date' },
];

function formatRegDate(v) {
  if (v == null || v === '') return '—';
  const ts = Number(v);
  const d = !isNaN(ts) && ts > 1000000000 ? new Date(ts * 1000) : new Date(v);
  if (isNaN(d)) return String(v);
  const dd   = String(d.getDate()).padStart(2, '0');
  const mm   = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  let   h    = d.getHours();
  const i    = String(d.getMinutes()).padStart(2, '0');
  const s    = String(d.getSeconds()).padStart(2, '0');
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${dd}/${mm}/${yyyy} ${h}:${i}:${s} ${ampm}`;
}

function formatCustomerValue(key, v) {
  if (v == null || v === '') return '—';
  if (key === 'regdate') return formatRegDate(v);
  return String(v);
}

function SectionCard({ icon, title, children, className = 'mb-3' }) {
  return (
    <CCard className={className}>
      <CCardHeader>
        <CIcon icon={icon} className="me-2 text-success" />{title}
      </CCardHeader>
      <CCardBody>{children}</CCardBody>
    </CCard>
  );
}

function FieldLabel({ icon, children }) {
  return (
    <CFormLabel className="d-flex align-items-center gap-2 mb-1">
      <CIcon icon={icon} size="sm" className="text-success" />{children}
    </CFormLabel>
  );
}

export default function JobFormPage() {
  const { jobcode } = useParams();
  const isEdit = !!jobcode;
  const navigate = useNavigate();
  const { subscribe } = useNotifications();
  const [form, setForm] = useState(EMPTY);
  const [history, setHistory] = useState([]);
  const [jobMeta, setJobMeta] = useState(null); // { time, sms } from the job row
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [smsModal, setSmsModal] = useState({ visible: false, hid: null });
  const [waModal,  setWaModal]  = useState({ visible: false, hid: null });
  const [customer, setCustomer] = useState(null);
  const [customerInfoOpen, setCustomerInfoOpen] = useState(false);
  const [invoice, setInvoice] = useState(null);       // { invoiceable, existingInvoice, sst } from API
  const [invForm, setInvForm] = useState({ description: '', subtotal: '', sstRate: '6' });
  const [invSaving, setInvSaving] = useState(false);
  const [invError, setInvError] = useState('');
  const [invCreated, setInvCreated] = useState(null);
  const [pointsInfo, setPointsInfo] = useState(null); // customer FMZ Points data
  const [pointsInput, setPointsInput] = useState('');
  const [pointsClaimed, setPointsClaimed] = useState(false); // already awarded for this job
  const [claimLoading, setClaimLoading] = useState(false);
  const [claimMsg, setClaimMsg] = useState(null); // { type: 'success'|'danger', text }

  // Job images
  const [jobImages, setJobImages]         = useState([]);
  const [imgLifetime, setImgLifetime]     = useState('30d');
  const [imgUploading, setImgUploading]   = useState(false);
  const [imgError, setImgError]           = useState('');
  const [imgPreview, setImgPreview]       = useState(null); // url to show in lightbox
  const imgInputRef                       = useRef(null);

  useEffect(() => {
    if (isEdit) return undefined;
    let cancelled = false;
    api.get('/jobs/suggested-jobcode')
      .then((r) => {
        if (cancelled) return;
        setForm((f) => {
          if (String(f.jobcode || '').trim()) return f;
          return { ...f, jobcode: r.data.jobcode || '' };
        });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isEdit]);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/jobs/${jobcode}`)
      .then(r => {
        const { job, history: hist, customer: cust } = r.data;
        setForm({
          custid: job.custid || '',
          jobcode: job.jobcode || '',
          perhubungan: job.perhubungan || [],
          keutamaan: job.keutamaan || 'Biasa',
          catatan: job.catatan || '',
          jenis: job.jenis || '',
          kos: job.kos || '',
          alat: job.alat || [],
          peralatan_lain: job.peralatan_lain || '',
          status: job.status || 'Baru',
        });
        setHistory(hist || []);
        setJobMeta({ time: job.time, sms: job.sms, admin_id: job.admin_id });
        setCustomer(cust || null);
        api.get(`/jobs/${job.jobcode}/images`).then(r => setJobImages(r.data.images || [])).catch(() => {});
        // Load invoice status for this job
        api.get(`/finance/job-invoice/${job.jobcode}`)
          .then(r => {
            setInvoice(r.data);
            if (r.data.sst) setInvForm(f => ({ ...f, sstRate: String(r.data.sst.rate) }));
          })
          .catch(() => {});
        // Load FMZ Points info for this customer + check if already claimed
        if (job.custid) {
          api.get('/finance/customer-points', { params: { custid: job.custid } })
            .then(r => {
              setPointsInfo(r.data);
              // Check if job_earn txn already exists for this jobcode
              if (r.data.registered) {
                api.get('/finance/points-history', { params: { search: job.jobcode, page: 1 } })
                  .then(h => {
                    const already = (h.data.txns || []).some(
                      t => t.type === 'job_earn' && t.ref_jobcode === job.jobcode && t.customer_uid === job.custid
                    );
                    setPointsClaimed(already);
                  })
                  .catch(() => {});
              }
            })
            .catch(() => {});
        }
      })
      .catch(() => setError('Failed to load job.'))
      .finally(() => setLoading(false));
  }, [jobcode, isEdit]);

  // Live-append history entries from WebSocket without page refresh
  useEffect(() => {
    if (!isEdit || !jobcode || !subscribe) return;
    return subscribe((msg) => {
      if (msg.type === 'job:updated' && msg.job?.jobcode === jobcode && msg.history) {
        setHistory(prev => {
          if (prev.some(h => h.hid === msg.history.id)) return prev;
          return [...prev, { ...msg.history, hid: msg.history.id }];
        });
        setForm(f => ({ ...f, status: msg.job.status }));
      }
    });
  }, [isEdit, jobcode, subscribe]);

  function handleChange(e) {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  }

  function toggleArray(field, value) {
    setForm(f => {
      const arr = f[field] || [];
      return { ...f, [field]: arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value] };
    });
  }

  async function searchCustomer(e) {
    e.preventDefault();
    if (!customerSearch.trim()) return;
    try {
      const { data } = await api.get('/customers', { params: { search: customerSearch } });
      setCustomerResults(data.customers);
    } catch {
      setError('Customer search failed.');
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true); setError(''); setSuccess('');
    try {
      if (isEdit) {
        const { jobcode: _jc, ...payload } = form;
        await api.put(`/jobs/${jobcode}`, payload);
        setSuccess('Job updated.');
        api.get(`/jobs/${jobcode}`).then((r) => setCustomer(r.data.customer || null)).catch(() => {});
      } else {
        const jc = String(form.jobcode || '').trim();
        if (jc) {
          try {
            await api.get(`/jobs/${encodeURIComponent(jc)}`);
            setError('This Receipt Number is already used by a job.');
            return;
          } catch (e1) {
            if (e1.response?.status !== 404) {
              setError(e1.response?.data?.message || 'Could not verify Receipt Number.');
              return;
            }
          }
          try {
            await api.get(`/warranty/${encodeURIComponent(jc)}`);
            setError('This Receipt Number is already used by a warranty claim.');
            return;
          } catch (e2) {
            if (e2.response?.status !== 404) {
              setError(e2.response?.data?.message || 'Could not verify Receipt Number.');
              return;
            }
          }
        }
        const payload = { ...form, jobcode: jc };
        const { data } = await api.post('/jobs', payload);
        setSuccess(`Job created: ${data.job.jobcode}`);
        let nextCode = '';
        try {
          const s = await api.get('/jobs/suggested-jobcode');
          nextCode = s.data.jobcode || '';
        } catch { /* ignore */ }
        setForm({ ...EMPTY, jobcode: nextCode });
        setCustomerResults([]);
      }
    } catch (err) {
      const msgs = err.response?.data?.errors?.map(e => e.msg).join(', ');
      setError(msgs || err.response?.data?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  async function handleCreateInvoice(e) {
    e.preventDefault();
    setInvError('');

    const discountPts = parseInt(pointsInput, 10) || 0;
    const pointsPerRm = pointsInfo?.points_per_rm || 100;
    if (discountPts > 0) {
      if (discountPts % pointsPerRm !== 0) {
        setInvError(`Points must be a multiple of ${pointsPerRm}.`);
        return;
      }
      if (pointsInfo && discountPts > pointsInfo.points) {
        setInvError(`Customer only has ${pointsInfo.points} points.`);
        return;
      }
    }

    setInvSaving(true);
    try {
      const sub = parseFloat(invForm.subtotal) || 0;
      const rate = invoice?.sst?.enabled ? (parseFloat(invForm.sstRate) || 0) : 0;
      const payload = {
        description: invForm.description.trim(),
        subtotal: sub,
        sst_rate: rate,
      };
      if (discountPts > 0) payload.discount_points = discountPts;

      const { data } = await api.post(`/finance/job-invoice/${jobcode}`, payload);
      setInvCreated(data.invoice);
      setInvoice(prev => ({ ...prev, existingInvoice: { id: data.invoice.id, invoice_no: data.invoice.invoice_no, total: data.invoice.total } }));
    } catch (err) {
      setInvError(err.response?.data?.message || 'Could not create invoice.');
    } finally {
      setInvSaving(false);
    }
  }

  async function handleClaimPoints() {
    setClaimLoading(true);
    setClaimMsg(null);
    try {
      const { data } = await api.post(`/finance/job-points/${jobcode}`);
      setClaimMsg({ type: 'success', text: data.message });
      setPointsClaimed(true);
      // Refresh points balance
      if (pointsInfo?.customer?.uid || form.custid) {
        api.get('/finance/customer-points', { params: { custid: form.custid } })
          .then(r => setPointsInfo(r.data))
          .catch(() => {});
      }
    } catch (err) {
      setClaimMsg({ type: 'danger', text: err.response?.data?.message || 'Failed to award points.' });
    } finally {
      setClaimLoading(false);
    }
  }

  async function handleImgUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (jobImages.length >= 4) { setImgError('Maximum 4 images per job.'); return; }
    setImgError(''); setImgUploading(true);
    try {
      const blob = await resizeImageClient(file);
      const fd = new FormData();
      fd.append('image', blob, file.name.replace(/\.[^.]+$/, '.jpg'));
      fd.append('lifetime', imgLifetime);
      const { data } = await api.post(`/jobs/${jobcode}/images`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setJobImages(prev => [...prev, data.image]);
    } catch (err) {
      setImgError(err.response?.data?.message || 'Upload failed.');
    } finally {
      setImgUploading(false);
      if (imgInputRef.current) imgInputRef.current.value = '';
    }
  }

  async function handleImgDelete(id) {
    if (!window.confirm('Remove this image?')) return;
    try {
      await api.delete(`/jobs/${jobcode}/images/${id}`);
      setJobImages(prev => prev.filter(i => i.id !== id));
    } catch {
      setImgError('Could not delete image.');
    }
  }

  if (loading) return <div className="text-center py-5"><CSpinner color="success" /></div>;

  return (
    <>
      <SmsModal
        jobcode={jobcode} hid={smsModal.hid} type="job"
        visible={smsModal.visible}
        onClose={() => setSmsModal({ visible: false, hid: null })}
        onSent={() => {
          setSmsModal({ visible: false, hid: null });
          api.get(`/jobs/${jobcode}`).then((r) => {
            setHistory(r.data.history || []);
            setJobMeta(m => ({ ...m, sms: 1 }));
            setCustomer(r.data.customer || null);
          });
        }}
      />
      <WhatsAppModal
        jobcode={jobcode} hid={waModal.hid} type="job"
        visible={waModal.visible}
        onClose={() => setWaModal({ visible: false, hid: null })}
        onSent={() => {
          api.get(`/jobs/${jobcode}`).then((r) => {
            setHistory(r.data.history || []);
            setJobMeta(m => ({ ...m, sms: 1 }));
          });
        }}
      />

      {/* Image lightbox */}
      <CModal visible={!!imgPreview} onClose={() => setImgPreview(null)} alignment="center" size="lg">
        <CModalHeader><CModalTitle>Image</CModalTitle></CModalHeader>
        <CModalBody className="text-center p-2">
          {imgPreview && <img src={imgPreview} alt="preview" style={{ maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain' }} />}
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" variant="outline" onClick={() => setImgPreview(null)}>Close</CButton>
        </CModalFooter>
      </CModal>

      <CModal visible={customerInfoOpen} onClose={() => setCustomerInfoOpen(false)} alignment="center" size="lg">
        <CModalHeader>
          <CModalTitle>
            <CIcon icon={cilInfo} className="me-2 text-success" />
            Customer info
          </CModalTitle>
        </CModalHeader>
        <CModalBody>
          {!customer ? (
            <p className="text-muted mb-0">No customer record linked to this job.</p>
          ) : (
            <CRow className="g-3 small">
              {CUSTOMER_INFO_FIELDS.map(({ key, label }) => (
                <CCol key={key} xs={12} md={6}>
                  <div className="text-muted text-uppercase" style={{ fontSize: 10, letterSpacing: '0.04em' }}>{label}</div>
                  <div className="fw-medium text-break" style={{ whiteSpace: key === 'catatan' ? 'pre-wrap' : undefined }}>
                    {formatCustomerValue(key, customer[key])}
                  </div>
                </CCol>
              ))}
            </CRow>
          )}
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" variant="outline" onClick={() => setCustomerInfoOpen(false)}>Close</CButton>
        </CModalFooter>
      </CModal>

      <CRow className="mb-3 align-items-center">
        <CCol><h5 className="mb-0">{isEdit ? `Edit Job: ${jobcode}` : 'New Job'}</h5></CCol>
        <CCol xs="auto" className="d-flex gap-2">
          {isEdit && (
            <>
              <CButton color="success" variant="outline" size="sm" onClick={() => setSmsModal({ visible: true, hid: null })}>
                <CIcon icon={cilSend} className="me-1" /> Send SMS
              </CButton>
              <CButton
                size="sm"
                variant="outline"
                style={{ borderColor: '#25d366', color: '#25d366' }}
                onClick={() => setWaModal({ visible: true, hid: null })}
                title="Open WhatsApp with customer number and the same default text as SMS (same as status history)"
              >
                <CIcon icon={cilSend} className="me-1" /> WhatsApp
              </CButton>
              <CButton
                color="secondary"
                variant="outline"
                size="sm"
                onClick={() => setCustomerInfoOpen(true)}
                title="View full customer record"
              >
                <CIcon icon={cilInfo} className="me-1" />
                Customer info
              </CButton>
            </>
          )}
          <CButton color="secondary" variant="outline" size="sm" onClick={() => navigate('/jobs')}>
            <CIcon icon={cilArrowLeft} className="me-1" /> Back
          </CButton>
        </CCol>
      </CRow>

      {error && <CAlert color="danger">{error}</CAlert>}
      {success && <CAlert color="success">{success}</CAlert>}

      <CForm onSubmit={handleSubmit}>
        <SectionCard icon={cilUser} title="Customer">
          {!isEdit ? (
            <>
              <FieldLabel icon={cilSearch}>Search Customer</FieldLabel>
              <CRow className="mb-2">
                <CCol md={8}>
                  <CFormInput
                    value={customerSearch}
                    onChange={e => setCustomerSearch(e.target.value)}
                    placeholder="Name or phone…"
                    onKeyDown={e => e.key === 'Enter' && searchCustomer(e)}
                  />
                </CCol>
                <CCol md={4}>
                  <CButton color="success" variant="outline" onClick={searchCustomer}>
                    <CIcon icon={cilSearch} className="me-1" /> Search
                  </CButton>
                </CCol>
              </CRow>
              {customerResults.length > 0 && (
                <div className="border rounded mt-1" style={{ maxHeight: 200, overflowY: 'auto', fontSize: 14 }}>
                  {customerResults.map(c => (
                    <div
                      key={c.uid}
                      className={`px-3 py-2 border-bottom ${form.custid === c.uid ? 'bg-success text-white' : ''}`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setForm(f => ({ ...f, custid: c.uid }))}
                    >
                      <strong>{c.nama}</strong> — {c.telhp}
                      {form.custid === c.uid && <span className="ms-2">✓</span>}
                    </div>
                  ))}
                </div>
              )}
              {form.custid && <div className="mt-2 text-success small">Selected: {form.custid}</div>}
            </>
          ) : (
            <>
              <FieldLabel icon={cilUser}>Customer ID</FieldLabel>
              <CFormInput value={form.custid} disabled />
            </>
          )}
        </SectionCard>

        <SectionCard icon={cilTask} title="Job Details">
          <CRow className="mb-3">
            <CCol xs={12}>
              <FieldLabel icon={cilTask}>Receipt Number <font color="red">*</font></FieldLabel>
              {isEdit ? (
                <CFormInput value={form.jobcode || jobcode} disabled />
              ) : (
                <CFormInput
                  name="jobcode"
                  value={form.jobcode}
                  onChange={handleChange}
                  maxLength={20}
                  autoComplete="off"
                  placeholder="123123123"
                />
              )}
              {!isEdit && (
                <div className="form-text small">
                  * Auto generated
                </div>
              )}
            </CCol>
          </CRow>
          <CRow className="mb-3">
            <CCol md={6}>
              <FieldLabel icon={cilTask}>Priority</FieldLabel>
              <select name="keutamaan" value={form.keutamaan} onChange={handleChange} className="form-select">
                {PRIORITY_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </CCol>
            <CCol md={6}>
              <FieldLabel icon={cilTask}>Status</FieldLabel>
              <select name="status" value={form.status} onChange={handleChange} className="form-select">
                {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </CCol>
          </CRow>
          <CRow>
            <CCol md={8}>
              <FieldLabel icon={cilLaptop}>Equipment Type / Description</FieldLabel>
              <CFormInput name="jenis" value={form.jenis} onChange={handleChange} maxLength={200} placeholder="e.g. Laptop Acer Aspire" />
            </CCol>
            <CCol md={4}>
              <FieldLabel icon={cilTask}>Cost (RM)</FieldLabel>
              <CFormInput name="kos" value={form.kos} onChange={handleChange} maxLength={10} placeholder="Optional" />
            </CCol>
          </CRow>
        </SectionCard>

        <SectionCard icon={cilLaptop} title="Equipment">
          <CFormLabel className="mb-2">Devices</CFormLabel>
          <div className="d-flex gap-3 flex-wrap mb-3">
            {EQUIPMENT_OPTIONS.map(opt => (
              <CFormCheck key={opt} label={opt} checked={form.alat.includes(opt)} onChange={() => toggleArray('alat', opt)} />
            ))}
          </div>
          <FieldLabel icon={cilLaptop}>Other Equipment</FieldLabel>
          <CFormInput name="peralatan_lain" value={form.peralatan_lain} onChange={handleChange} maxLength={100} placeholder="Describe other items…" />
        </SectionCard>

        <SectionCard icon={cilPhone} title="Contact Methods">
          <div className="d-flex gap-3 flex-wrap">
            {CONTACT_OPTIONS.map(opt => (
              <CFormCheck key={opt} label={opt} checked={form.perhubungan.includes(opt)} onChange={() => toggleArray('perhubungan', opt)} />
            ))}
          </div>
        </SectionCard>

        <SectionCard icon={cilNotes} title="Notes" className="mb-4">
          <CFormTextarea name="catatan" value={form.catatan} onChange={handleChange} rows={3} placeholder="Additional notes…" />
        </SectionCard>

        <div className="d-flex gap-2 mb-4">
          <CButton
            type="submit"
            color="success"
            disabled={saving || (!isEdit && !form.custid)}
          >
            <CIcon icon={cilSave} className="me-1" />
            {saving ? 'Saving…' : isEdit ? 'Update Job' : 'Create Job'}
          </CButton>
          <CButton color="secondary" variant="outline" onClick={() => navigate('/jobs')}>Cancel</CButton>
        </div>
      </CForm>

      {/* FMZ Points — visible to all staff when customer has a loyalty account */}
      {isEdit && pointsInfo?.registered && pointsInfo?.loyalty_enabled && (
        <CCard className="mb-3">
          <CCardHeader>
            <CIcon icon={cilGift} className="me-2 text-warning" />FMZ Points
          </CCardHeader>
          <CCardBody>
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <div>
                <div className="small text-muted mb-1">Customer balance</div>
                <CBadge color="warning" style={{ fontSize: 14, padding: '6px 12px' }}>
                  {pointsInfo.points} pts
                </CBadge>
              </div>
              <div className="flex-grow-1">
                {pointsClaimed ? (
                  <div className="d-flex align-items-center gap-2 text-success small">
                    <span>✓</span> Points already claimed for this job.
                  </div>
                ) : (
                  <CButton
                    color="warning"
                    size="sm"
                    disabled={claimLoading}
                    onClick={handleClaimPoints}
                  >
                    {claimLoading ? <CSpinner size="sm" /> : <><CIcon icon={cilGift} className="me-1" />Claim Points for Customer</>}
                  </CButton>
                )}
              </div>
            </div>
            {claimMsg && (
              <CAlert color={claimMsg.type} className="mt-3 mb-0 py-2 small">
                {claimMsg.text}
              </CAlert>
            )}
          </CCardBody>
        </CCard>
      )}

      {isEdit && invoice && (
        <CCard className="mb-3">
          <CCardHeader>
            <CIcon icon={cilMoney} className="me-2 text-success" />Invoice
          </CCardHeader>
          <CCardBody>
            {invoice.existingInvoice ? (
              <div className="d-flex align-items-center gap-3">
                <div>
                  <div className="fw-semibold">{invoice.existingInvoice.invoice_no}</div>
                  <div className="text-muted small">Total: RM {Number(invoice.existingInvoice.total).toFixed(2)}</div>
                </div>
                {invCreated && <span className="text-success small">✓ Created</span>}
              </div>
            ) : !invoice.invoiceable ? (
              <div className="text-muted small">Invoice can only be created for: Selesai, Menunggu Diambil, Dalam Proses, or Waiting to Pickup.</div>
            ) : (
              <CForm onSubmit={handleCreateInvoice}>
                {invError && <CAlert color="danger" className="mb-3">{invError}</CAlert>}
                <CRow className="g-2">
                  <CCol xs={12}>
                    <CFormLabel className="small fw-semibold mb-1">Description</CFormLabel>
                    <CFormInput
                      placeholder="Parts, labour, scope of work…"
                      value={invForm.description}
                      onChange={e => setInvForm(f => ({ ...f, description: e.target.value }))}
                    />
                  </CCol>
                  <CCol md={4}>
                    <CFormLabel className="small fw-semibold mb-1">Amount (RM)</CFormLabel>
                    <CFormInput
                      type="number" inputMode="decimal" min="0" step="0.01" required
                      value={invForm.subtotal}
                      onChange={e => setInvForm(f => ({ ...f, subtotal: e.target.value }))}
                    />
                  </CCol>
                  {invoice.sst?.enabled && (
                    <CCol md={3}>
                      <CFormLabel className="small fw-semibold mb-1">SST (%)</CFormLabel>
                      <CFormInput
                        type="number" inputMode="decimal" min="0" max="100" step="0.01"
                        value={invForm.sstRate}
                        onChange={e => setInvForm(f => ({ ...f, sstRate: e.target.value }))}
                      />
                    </CCol>
                  )}
                  <CCol md={invoice.sst?.enabled ? 5 : 8} className="d-flex align-items-end">
                    {(() => {
                      const sub = parseFloat(invForm.subtotal) || 0;
                      const rate = invoice.sst?.enabled ? (parseFloat(invForm.sstRate) || 0) : 0;
                      const pointsPerRm = pointsInfo?.points_per_rm || 100;
                      const discountPts = parseInt(pointsInput, 10) || 0;
                      const discountRm = discountPts > 0 ? Math.round(discountPts / pointsPerRm * 100) / 100 : 0;
                      const sst = Math.round(sub * rate) / 100;
                      const total = Math.max(0, Math.round((sub + sst - discountRm) * 100) / 100);
                      return (
                        <div className="small text-muted">
                          {invoice.sst?.enabled && <div>SST: RM {sst.toFixed(2)}</div>}
                          {discountRm > 0 && <div className="text-success">Discount: − RM {discountRm.toFixed(2)}</div>}
                          <div className="fw-bold text-success">Total: RM {total.toFixed(2)}</div>
                        </div>
                      );
                    })()}
                  </CCol>

                  {/* FMZ Points coupon */}
                  {pointsInfo?.registered && pointsInfo?.loyalty_enabled && (
                    <CCol xs={12}>
                      <hr className="my-1" />
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <CIcon icon={cilGift} className="text-warning" />
                        <span className="small fw-semibold">Apply FMZ Points coupon</span>
                        <CBadge color="warning">{pointsInfo.points} pts</CBadge>
                      </div>
                      <CRow className="g-2 align-items-end">
                        <CCol md={5}>
                          <CFormLabel className="small mb-1">
                            Points to apply (multiples of {pointsInfo.points_per_rm})
                          </CFormLabel>
                          <CFormInput
                            type="number" min="0" step={pointsInfo.points_per_rm}
                            value={pointsInput}
                            onChange={e => setPointsInput(e.target.value)}
                            placeholder={`e.g. ${pointsInfo.points_per_rm}`}
                          />
                        </CCol>
                        <CCol md={7} className="small text-muted pt-2">
                          {parseInt(pointsInput, 10) > 0
                            ? `${pointsInput} pts = RM${(parseInt(pointsInput, 10) / pointsInfo.points_per_rm).toFixed(2)} off`
                            : `${pointsInfo.points_per_rm} pts = RM1.00 off`}
                        </CCol>
                      </CRow>
                    </CCol>
                  )}
                </CRow>
                <div className="mt-3">
                  <CButton color="success" type="submit" size="sm" disabled={invSaving}>
                    {invSaving ? <CSpinner size="sm" /> : <><CIcon icon={cilMoney} className="me-1" />Generate Invoice</>}
                  </CButton>
                </div>
              </CForm>
            )}
          </CCardBody>
        </CCard>
      )}

      {isEdit && (
        <CCard className="mb-3">
          <CCardHeader>
            <CIcon icon={cilImage} className="me-2 text-success" />Job Images
            <span className="ms-2 text-muted small">({jobImages.length}/4)</span>
          </CCardHeader>
          <CCardBody>
            {imgError && <CAlert color="danger" className="py-2 mb-3 small">{imgError}</CAlert>}

            {/* Thumbnails */}
            {jobImages.length > 0 && (
              <div className="d-flex flex-wrap gap-3 mb-3">
                {jobImages.map(img => (
                  <div key={img.id} style={{ width: 110 }}>
                    <div style={{ position: 'relative', width: 110, height: 110 }}>
                      <img
                        src={img.url}
                        alt={img.original_name}
                        style={{ width: 110, height: 110, objectFit: 'cover', borderRadius: 6, cursor: 'pointer', border: '1px solid #dee2e6' }}
                        onClick={() => setImgPreview(img.url)}
                        title={img.original_name}
                      />
                      <CButton
                        size="sm" color="danger"
                        style={{ position: 'absolute', top: 3, right: 3, padding: '1px 5px', lineHeight: 1, opacity: 0.85 }}
                        onClick={() => handleImgDelete(img.id)}
                        title="Remove"
                      >
                        <CIcon icon={cilTrash} size="sm" />
                      </CButton>
                    </div>
                    <div style={{ fontSize: 10, color: '#888', textAlign: 'center', marginTop: 4 }}>
                      {img.expires_at ? `Expires: ${img.expires_at}` : 'Permanent'}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {jobImages.length < 4 && (
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <select className="form-select form-select-sm" style={{ width: 'auto' }} value={imgLifetime} onChange={e => setImgLifetime(e.target.value)}>
                  <option value="7d">7 days</option>
                  <option value="14d">2 weeks</option>
                  <option value="30d">1 month</option>
                  <option value="unlimited">Unlimited</option>
                </select>
                <input ref={imgInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImgUpload} />
                <CButton
                  color="success" variant="outline" size="sm"
                  disabled={imgUploading}
                  onClick={() => imgInputRef.current?.click()}
                >
                  {imgUploading ? <CSpinner size="sm" /> : <><CIcon icon={cilImage} className="me-1" />Upload Image</>}
                </CButton>
              </div>
            )}
          </CCardBody>
        </CCard>
      )}

      {isEdit && jobMeta && (
        <CCard>
          <CCardHeader>
            <CIcon icon={cilNotes} className="me-2 text-success" />Status History
          </CCardHeader>
          <CCardBody>
            {/* Creation row — always shown, matches old Laravel "Pendaftaran kerja baru" */}
            <div className="border-bottom py-2 d-flex align-items-center justify-content-between gap-2">
              <div style={{ fontSize: 14 }}>
                <span className="text-muted small me-2">
                  {new Date(parseInt(jobMeta.time) * 1000).toLocaleString()}
                </span>
                Pendaftaran kerja baru.
              </div>
              {!jobMeta.sms && (
                <div className="d-flex gap-1 flex-shrink-0">
                  <CButton size="sm" color="success" variant="outline" onClick={() => setSmsModal({ visible: true, hid: null })}>
                    <CIcon icon={cilSend} className="me-1" /> SMS
                  </CButton>
                  <CButton size="sm" variant="outline" style={{ borderColor: '#25d366', color: '#25d366' }} onClick={() => setWaModal({ visible: true, hid: null })}>
                    <CIcon icon={cilSend} className="me-1" /> WhatsApp
                  </CButton>
                </div>
              )}
            </div>
            {/* Subsequent status-change entries (skip any "created" entry inserted by the new backend) */}
            {history.filter(h => !/created with status/i.test(h.description || '')).map(h => (
              <div key={h.hid} className="border-bottom py-2 d-flex align-items-center justify-content-between gap-2">
                <div style={{ fontSize: 14 }}>
                  <span className="text-muted small me-2">
                    {new Date(parseInt(h.time) * 1000).toLocaleString()}
                  </span>
                  <span dangerouslySetInnerHTML={{ __html: h.description }} />
                </div>
                {h.needsms === 1 && h.smssent === 0 && (
                  <div className="d-flex gap-1 flex-shrink-0">
                    <CButton size="sm" color="success" variant="outline" onClick={() => setSmsModal({ visible: true, hid: h.hid })}>
                      <CIcon icon={cilSend} className="me-1" /> SMS
                    </CButton>
                    <CButton size="sm" variant="outline" style={{ borderColor: '#25d366', color: '#25d366' }} onClick={() => setWaModal({ visible: true, hid: h.hid })}>
                      <CIcon icon={cilSend} className="me-1" /> WhatsApp
                    </CButton>
                  </div>
                )}
              </div>
            ))}
          </CCardBody>
        </CCard>
      )}

      {isEdit && jobMeta?.admin_id && (
        <div className="text-muted small mt-2 text-end">
          Created by: <strong>{jobMeta.admin_id}</strong>
        </div>
      )}
    </>
  );
}
